/**
 * RoleGuard Component
 *
 * Wraps a route element and renders it only when the authenticated user's
 * role is in the `allowedRoles` list. Otherwise redirects to a safe fallback.
 *
 * This is defense-in-depth in addition to RLS at the DB layer and the
 * sidebar role filter. It prevents a user from reaching admin-only pages
 * by typing the URL directly.
 *
 * NOTE: PrivateLayout already gates "not authenticated" -> /login and
 * "technician outside allow-list" -> /tecnico/dashboard, so by the time a
 * route element renders, the user is authenticated and (if technician) on
 * an allowed path. RoleGuard then adds the role-membership check.
 *
 * Usage:
 *   <Route path=":id/editar" element={<RoleGuard allowedRoles={['admin']}><UserForm /></RoleGuard>} />
 *
 * @module components/common/RoleGuard
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const RoleGuard = ({ allowedRoles, children, fallbackPath = '/dashboard' }) => {
  const { profile, loading } = useAuth();
  const location = useLocation();

  // While the profile is still loading, render nothing — PrivateLayout already
  // shows the spinner above us. We don't want to flash a redirect before the
  // role is known.
  if (loading || !profile) return null;

  const role = profile.role;

  if (allowedRoles.includes(role)) {
    return children;
  }

  // Avoid an infinite redirect loop if the fallback itself isn't reachable.
  if (location.pathname === fallbackPath) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 text-muted">
        <i className="bi bi-shield-lock me-2" aria-hidden="true" />
        Acceso no autorizado para tu rol.
      </div>
    );
  }

  return <Navigate to={fallbackPath} replace state={{ from: location, reason: 'role_denied' }} />;
};

RoleGuard.propTypes = {
  allowedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
  fallbackPath: PropTypes.string,
};

export default RoleGuard;
